import {Ng2StateDeclaration, ResolvableLiteral, Transition, UIView} from '@uirouter/angular';
import {
    accountResolver,
    authDocumentsDownloadResolver,
    authJwtResolver,
    authResolver,
    circuitResolver,
    dashboardResolver,
    DefaultState,
    dossierResolver,
    messageResolver,
    nextTaskResolver,
    PesAccountService,
    PesCircuitDetailsComponent,
    PesCircuitsComponent,
    PesCompanyCreateComponent,
    PesCompanyDetailsComponent,
    PesCompanyDetailsEditComponent,
    PesCompanyDetailsLogoComponent,
    PesCompanyDivisionAddComponent,
    PesCompanyDivisionAddUserComponent,
    PesCompanyDivisionDetailsComponent,
    PesCompanyDivisionEditComponent,
    PesCompanyEmailFooterEditorComponent,
    PesCompanyEmailLogoEditorComponent,
    PesCompanyEmailPreviewComponent,
    PesCompanyEmailSubjectEditorComponent,
    PesCompanyEmailTemplateManagerComponent,
    PesCompanyImportComponent,
    PesCompanyListComponent,
    PesCompanyListDivisionsComponent,
    PesDashboardComponent,
    PesDocumentsDownloadComponent,
    PesEmailTemplateManagerComponent,
    PesEntityLogoComponent,
    PesFileManagerComponent,
    PesForbiddenComponent,
    PesPageCompanyEmailTemplateEditorComponent,
    PesPasswordForgetComponent,
    PesPasswordRecoverComponent,
    PesPofilesComponent,
    PesRolesComponent,
    PesStyleCreateComponent,
    PesStyleDetailsEditComponent,
    PesStyleDetailsManagerComponent,
    PesStyleListManagerComponent,
    PesStylePreviewComponent,
    PesTemplateCreateCircuitComponent,
    PesTemplateDetailsComponent,
    PesTemplatesComponent,
    PesUserCreateComponent,
    PesUserDetailsComponent,
    PesUserImportComponent,
    PesUserListComponent,
    rightResolver,
    Rights,
    taskResolver,
    typeResolver
} from '@pes/ui';
import {LbpaTaskComponent} from '../component/task/lbpa-task.component';
import {LoginComponent} from '../component/pes-login/login.component';
import {MessageComponent} from '../component/pes-message/pes-message.component';
import {CircuitsFrontOfficeComponent} from '../component/circuit-list-front-office/circuits-front-office.component';
import {AppStateProvider} from './provider/state.provider';
import {LbpaCircuitDetailsComponent} from '../component/lbpa-circuit-details/lbpa-circuit-details.component';

// ----------- RESOLVERS ------------

const ACCOUNT_RESOLVER: ResolvableLiteral = Object.create(accountResolver);
const AUTH_RESOLVER: ResolvableLiteral = Object.create(authResolver);
const AUTH_JWT_RESOLVER: ResolvableLiteral = Object.create(authJwtResolver);
const RIGHT_RESOLVER: ResolvableLiteral = Object.create(rightResolver);
const TASK_RESOLVER: ResolvableLiteral = Object.create(taskResolver);
const DOSSIER_RESOLVER: ResolvableLiteral = Object.create(dossierResolver);
const NEXT_TASK_RESOLVER: ResolvableLiteral = Object.create(nextTaskResolver);
const MESSAGE_RESOLVER: ResolvableLiteral = Object.create(messageResolver);
const TYPE_RESOLVER: ResolvableLiteral = Object.create(typeResolver);
const CIRCUIT_RESOLVER: ResolvableLiteral = Object.create(circuitResolver);
const AUTH_DOCUMENTS_DOWNLOAD_RESOLVER: ResolvableLiteral = Object.create(authDocumentsDownloadResolver);
const DASHBOARD_RESOLVER: ResolvableLiteral = Object.create(dashboardResolver);
const REDIRECTION_RESOLVER: ResolvableLiteral = Object.create({
    token: 'redirection',
    deps: [Transition],
    resolveFn: transition => decodeURIComponent(transition.params().redirection)
});
const FO_REDIRECTION_RESOLVER: ResolvableLiteral = Object.create({
    token: 'fo_redirection',
    deps: [AppStateProvider, PesAccountService, Transition, 'authJwt'],
    resolveFn: (state: AppStateProvider, account: PesAccountService, transition: Transition) => {
        transition.onSuccess({}, () => {
            sessionStorage.setItem('alertExpiration', 'hide');
            state.onLoginSuccess(account.identity)
        });
    }
});

const CONTRACTTYPE_RESOLVER: ResolvableLiteral = Object.create({
    token: 'contractType',
    deps: [Transition],
    resolveFn: transition => decodeURIComponent(transition.params().contractType)
});

// ----------- STATES ------------

export const APP_STATES: Ng2StateDeclaration[] = [

    // ----------- MAIN CONTAINER --------------------------------------------------------------------------------------
    // --> Application main container
    {
        name: DefaultState.APPLICATION,
        abstract: true,
        component: UIView,
        resolve: [ACCOUNT_RESOLVER]
    },

    // ----------- ACCOUNT VIEW ----------------------------------------------------------------------------------------
    /*{
        name: 'FutureState.ACCOUNT',
        url: '/account',
        loadChildren: '../../../lib/pes-account/account.module#PesAccountModule'
    },*/

    // ----------- LOGIN VIEW ------------------------------------------------------------------------------------------
    {
        name: DefaultState.LOGIN,
        parent: DefaultState.APPLICATION,
        url: '/login',
        component: LoginComponent
    },

    // ----------- PASSWORD --------------------------------------------------------------------------------------------
    // --> Used for sending new password to users
    {
        name: DefaultState.PASSWORD_FORGET,
        parent: DefaultState.APPLICATION,
        url: '/password-forget/:credential',
        component: PesPasswordForgetComponent
    },
    // --> Used for user activation and password recovering
    {
        name: DefaultState.RECOVER,
        parent: DefaultState.APPLICATION,
        url: '/recover/:key',
        component: PesPasswordRecoverComponent
    },

    // ----------- FORBIDDEN SCREENSHOT --------------------------------------------------------------------------------
    {
        name: DefaultState.FORBIDDEN,
        parent: DefaultState.APPLICATION,
        url: '/forbidden',
        component: PesForbiddenComponent
    },

    // ----------- PES MESSAGE VIEW ------------------------------------------------------------------------------------
    {
        name: DefaultState.MESSAGE,
        url: '/message?message&type&redirection&healthData&contractType',
        parent: DefaultState.APPLICATION,
        component: MessageComponent,
        resolve: [MESSAGE_RESOLVER, TYPE_RESOLVER, REDIRECTION_RESOLVER, CONTRACTTYPE_RESOLVER]
    },
    {
        name: 'saml-auth',
        url: '/saml-auth?token',
        parent: DefaultState.APPLICATION,
        resolve: [AUTH_JWT_RESOLVER, FO_REDIRECTION_RESOLVER]
    },

    // ----------- TASKS -----------------------------------------------------------------------------------------------
    // --> Default task view
    {
        name: DefaultState.TASK,
        url: '/task?taskReference&stepIdx&docIdx&pos&tpl',
        parent: DefaultState.APPLICATION,
        component: LbpaTaskComponent,
        resolve: [AUTH_RESOLVER, TASK_RESOLVER]
    },
    // --> Task view with authentication
    //     see: screens property in the _base.entity.json
    {
        name: DefaultState.TASK_WITH_AUTH,
        url: '/task-with-auth/:dossierReference?token&pos&tpl',
        parent: DefaultState.APPLICATION,
        component: LbpaTaskComponent,
        resolve: [AUTH_JWT_RESOLVER, DOSSIER_RESOLVER, NEXT_TASK_RESOLVER]
    },
    // --> Allows to go to the next valid task from the application
    {
        name: DefaultState.NEXT_TASK,
        url: '/next/:dossierReference',
        parent: DefaultState.APPLICATION,
        component: UIView,
        resolve: [AUTH_RESOLVER, DOSSIER_RESOLVER, NEXT_TASK_RESOLVER]
    },

    // ----------- DOWNLOAD VIEWS --------------------------------------------------------------------------------------
    // --> Parent downloads container
    {
        name: DefaultState.DOWNLOAD,
        url: '/download',
        parent: DefaultState.APPLICATION,
        component: UIView,
        abstract: true,
        resolve: [AUTH_RESOLVER]
    },
    // --> Allows users to download circuit documents
    {
        name: DefaultState.DOWNLOAD_DOCUMENTS,
        url: '/documents/:circuitId',
        component: PesDocumentsDownloadComponent,
        data: {right: 'CIRCUIT_READ'},
        resolve: [RIGHT_RESOLVER, CIRCUIT_RESOLVER]
    },
    // --> Parent downloads container with authentication : redirects to the download.documents state
    //     see: screens property in the _base.entity.json
    {
        name: DefaultState.DOWNLOAD_WITH_AUTH,
        url: '/download-with-auth/:circuitId?token',
        parent: DefaultState.APPLICATION,
        component: UIView,
        resolve: [AUTH_JWT_RESOLVER, AUTH_DOCUMENTS_DOWNLOAD_RESOLVER]
    },

    // ----------- BACK OFFICE -----------------------------------------------------------------------------------------
    // --> Parent BO container
    {
        name: DefaultState.BACK_OFFICE,
        url: '/bo',
        parent: DefaultState.APPLICATION,
        abstract: true,
        component: UIView,
        redirectTo: DefaultState.DASHBOARD
    },
    // --> Dashboard view
    {
        name: DefaultState.DASHBOARD,
        parent: DefaultState.APPLICATION,
        url: '/dashboard',
        component: PesDashboardComponent,
        data: {right: Rights.DASHBOARD},
        resolve: [RIGHT_RESOLVER, AUTH_RESOLVER, DASHBOARD_RESOLVER]
    },
    // --> Admin circuit list (BO)
    {
        name: DefaultState.BO_CIRCUITS,
        url: '/circuits?status',
        component: PesCircuitsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.CIRCUIT_LIST}
    },
    // --> Admin circuit details (BO)
    {
        name: DefaultState.BO_CIRCUITS_DETAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/circuits/{circuitRef}',
        component: PesCircuitDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.CIRCUIT_READ}
    },
    // --> Template list view (BO)
    {
        name: DefaultState.BO_TEMPLATES,
        url: '/templates',
        component: PesTemplatesComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.TEMPLATE_LIST}
    },
    // --> Template details view (BO)
    {
        name: DefaultState.BO_TEMPLATES_DETAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/templates/{templateRef}',
        component: PesTemplateDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.TEMPLATE_LIST}
    },
    // --> Template creation view (BO)
    {
        name: DefaultState.BO_TEMPLATES_CREATE_CIRCUIT,
        parent: DefaultState.BACK_OFFICE,
        url: '/templates/{templateRef}/create-circuit',
        component: PesTemplateCreateCircuitComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.CIRCUIT_CREATE, useReference: true}
    },
    // --> Users management
    {
        name: DefaultState.BO_USERS,
        url: '/users',
        component: PesUserListComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.USER_LIST}
    },
    {
        name: DefaultState.BO_USERS_DETAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/users/:userId',
        component: PesUserDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.USER_UPDATE}
    },
    {
        name: DefaultState.BO_USERS_CREATE,
        parent: DefaultState.BACK_OFFICE,
        url: '/users/create',
        component: PesUserCreateComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.USER_CREATE}
    },
    {
        name: DefaultState.BO_USERS_IMPORT,
        parent: DefaultState.BACK_OFFICE,
        url: '/users/import',
        component: PesUserImportComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.USER_IMPORT}
    },
    // --> Company management
    {
        name: DefaultState.BO_COMPANIES,
        url: '/companies',
        component: PesCompanyListComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_LIST}
    },
    {
        name: DefaultState.BO_COMPANY_CREATE,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/create',
        component: PesCompanyCreateComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_UPDATE}
    },
    {
        name: DefaultState.BO_COMPANY_DETAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef?:companyName',
        component: PesCompanyDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_UPDATE}
    },
    {
        name: DefaultState.BO_COMPANY_DETAILS_FORM,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/details?:companyName',
        component: PesCompanyDetailsEditComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_UPDATE}
    },
    {
        name: DefaultState.BO_COMPANY_DETAILS_LOGO,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/logo',
        component: PesCompanyDetailsLogoComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_UPDATE}
    },
    {
        name: DefaultState.BO_COMPANY_IMPORT,
        parent: DefaultState.BACK_OFFICE,
        url: '/company/import',
        component: PesCompanyImportComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_CREATE}
    },
    // --> Division management
    {
        name: DefaultState.BO_COMPANY_LIST_DIVISIONS,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/divisions',
        component: PesCompanyListDivisionsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_LIST}
    },
    {
        name: DefaultState.BO_COMPANY_DIVISION_ADD,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/add-division/:divisionRef',
        component: PesCompanyDivisionAddComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_CREATE}
    },
    {
        name: DefaultState.BO_COMPANY_DIVISION_DETAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/division/:divisionRef',
        component: PesCompanyDivisionDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_READ}
    },
    {
        name: DefaultState.BO_COMPANY_DIVISION_EDIT,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/edit-division/:divisionRef',
        component: PesCompanyDivisionEditComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_UPDATE}
    },
    {
        name: DefaultState.BO_COMPANY_DIVISION_ADD_USER,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/division/:divisionRef/add-user',
        component: PesCompanyDivisionAddUserComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_ADD_USER}
    },
    {
        name: DefaultState.BO_COMPANY_DIVISION_DELETE,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.GROUP_DELETE}
    },
    // --> Sub Division management
    {
        name: DefaultState.BO_COMPANY_SUB_DIVISION_ADD,
        parent: DefaultState.BACK_OFFICE,
        url: '/companies/:companyRef/add-sub-division/:divisionRef',
        component: PesCompanyDivisionAddComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.SUB_GROUP_CREATE}
    },
    // --> Mails management
    {
        name: DefaultState.BO_EDIT_EMAILS,
        url: '/edit/emails',
        component: PesEmailTemplateManagerComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_LIST}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAILS,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/:groupName?id',
        component: PesCompanyEmailTemplateManagerComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_READ}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAIL_BODY,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/edit-body/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesPageCompanyEmailTemplateEditorComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.MAIL_MANAGE_TEMPLATE}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAIL_SUBJECT,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/edit-subject/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesCompanyEmailSubjectEditorComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.MAIL_MANAGE_TEMPLATE}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAIL_FOOTER,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/edit-footer/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesCompanyEmailFooterEditorComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.MAIL_MANAGE_TEMPLATE}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAIL_LOGO,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/edit-logo/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesCompanyEmailLogoEditorComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.ASSET_WRITE}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_LIST_EMAIL_PREVIEW,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/emails-preview/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesCompanyEmailPreviewComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.MAIL_MANAGE_TEMPLATE, backButtonState: DefaultState.BO_EDIT_COMPANY_EMAILS}
    },
    {
        name: DefaultState.BO_EDIT_COMPANY_EMAIL_PREVIEW,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/emails/emails-preview/:templateName?:entityRef&groupRef&groupName&locale',
        component: PesCompanyEmailPreviewComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.MAIL_MANAGE_TEMPLATE}
    },
    // --> Styles management
    {
        name: DefaultState.BO_LIST_STYLE,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/styles',
        component: PesStyleListManagerComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_LIST}
    },
    {
        name: DefaultState.BO_EDIT_STYLES,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/styles/:styleRef',
        component: PesStyleDetailsManagerComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_READ}
    },
    {
        name: DefaultState.BO_EDIT_STYLE_TYPE,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/style-type/:styleRef?:type',
        component: PesStyleDetailsEditComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_READ}
    },
    {
        name: DefaultState.BO_EDIT_STYLE_PREVIEW,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/style-preview/:styleRef',
        component: PesStylePreviewComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_READ}
    },
    {
        name: DefaultState.BO_CREATE_STYLE,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/style-create',
        component: PesStyleCreateComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.COMPANY_READ}
    },
    // --> Entity logo management
    {
        name: DefaultState.ADMIN_EDIT_ENTITY_LOGO,
        parent: DefaultState.BACK_OFFICE,
        url: '/edit/entity-logo',
        component: PesEntityLogoComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.ENTITY_ADMIN}
    },

    // ----------- FRONT OFFICE ----------------------------------------------------------------------------------------
    // --> Parent FO container
    {
        name: DefaultState.FRONT_OFFICE,
        url: '/fo',
        parent: DefaultState.APPLICATION,
        abstract: true,
        component: UIView,
        redirectTo: DefaultState.FO_CIRCUITS
    },
    // --> Admin circuit list (FO)
    {
        name: DefaultState.FO_CIRCUITS,
        url: '/circuits?status',
        component: CircuitsFrontOfficeComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.CIRCUIT_MANAGEMENT, showBreadcrumb: false, showBackButton: false}
    },
    // --> Admin circuit details (FO)
    {
        name: DefaultState.FO_CIRCUITS_DETAILS,
        parent: DefaultState.FRONT_OFFICE,
        url: '/circuits/{circuitRef}',
        component: LbpaCircuitDetailsComponent,
        resolve: [RIGHT_RESOLVER],
        data: {right: Rights.CIRCUIT_UPDATE, backButtonState: DefaultState.FO_CIRCUITS, showBreadcrumb: false}
    },

    // ----------- SANDBOX ---------------------------------------------------------------------------------------------
    // --> Only use for components development
    /*{
        name: DefaultState.SANDBOX,
        url: '/sandbox',
        parent: DefaultState.APPLICATION,
        component: SandboxComponent,
        resolve: [AUTH_RESOLVER]
    },*/

    // ----------- DEV TOOLS -------------------------------------------------------------------------------------------
    // -> Profiles manager
    {
        name: DefaultState.PROFILES,
        url: '/profiles',
        component: PesPofilesComponent,
        resolve: [AUTH_RESOLVER]
    },
    // -> Roles manager
    {
        name: DefaultState.ROLES,
        url: '/roles',
        component: PesRolesComponent,
        resolve: [AUTH_RESOLVER]
    },

    // ----------- FILE MANAGER ----------------------------------------------------------------------------------------
    {
        name: 'files',
        url: '/files',
        component: PesFileManagerComponent,
        data: {right: Rights.FILE_ALL},
        parent: DefaultState.BACK_OFFICE
    }

    // ----------- STATS -----------------------------------------------------------------------------------------------
    /*{
        name: DefaultState.STATS,
        url: '/stats',
        component: PesStatsComponent,
        resolve: [AUTH_RESOLVER]
    }*/
];



// WEBPACK FOOTER //
// ./src/app/config/app.states.ts